/**
 * Loads the Google Maps JavaScript API with the key served by ProjectManagementServices
 * (`GoogleMaps/getmapsconfig`), instead of a key hard-coded in index.html.
 *
 * Started from main.ts before bootstrap and never awaited there, so app start and the
 * login page are not delayed. The key is held in memory only — nothing is written to
 * browser storage. Map routes wait for it through `googleMapsReadyGuard`.
 */

const MAPS_SCRIPT_URL = 'https://maps.googleapis.com/maps/api/js';

let mapsReady: Promise<void> | null = null;

export function startGoogleMapsLoad(projectManagementApiUrl: string): void {
  mapsReady ??= fetchKey(projectManagementApiUrl).then(injectMapsScript);
}

/** Resolves once the Maps script has loaded (or failed), or after `timeoutMs`, whichever is first. Never rejects. */
export function waitForGoogleMaps(timeoutMs: number): Promise<void> {
  if (!mapsReady) return Promise.resolve();
  return Promise.race([
    mapsReady,
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);
}

async function fetchKey(projectManagementApiUrl: string): Promise<string> {
  try {
    const response = await fetch(`${projectManagementApiUrl}GoogleMaps/getmapsconfig`);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const config: { apiKey?: string } = await response.json();
    return config.apiKey ?? '';
  } catch (err) {
    console.error('Google Maps API key could not be loaded:', err);
    return '';
  }
}

// Same script and attributes as the tag that used to live in index.html. Without a key the
// script still loads, so a failed lookup shows Google's own error map instead of a page error.
function injectMapsScript(apiKey: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const keyParam = apiKey ? `key=${encodeURIComponent(apiKey)}&` : '';
    const script = document.createElement('script');
    script.src = `${MAPS_SCRIPT_URL}?${keyParam}libraries=places&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}
